import cx from 'classnames'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from 'components/ModalSheet'
import { observer } from 'mobx-react'
import React from 'react'
import styles from './ApplyCouponAsAdmin.sass'
import ApplyCouponAsAdminViewModel from './ApplyCouponAsAdminViewModel'
import Button from 'components/Button'
import { useInfiniteScroll } from 'hooks/infinite-scroll'
import Spinner from 'components/Spinner'
import Coupon from 'billing/models/Coupon'
import { formatMoney } from 'utils/money-util'
import Icon from 'components/Icon'
import { Id } from 'types/definitions'
import SearchField from 'components/SearchField'

interface ApplyCouponAsAdminProps {
  vm: ApplyCouponAsAdminViewModel
}

export default observer(function ApplyCouponAsAdminDialog({
  vm,
}: ApplyCouponAsAdminProps) {
  const scrollMonitorRef = React.useRef(null)
  useInfiniteScroll(scrollMonitorRef, vm.scrollProvider)

  const disabled = vm.applyCouponAsAdmin.pending

  return (
    <ModalSheet
      showing={vm.showing}
      onRequestClose={vm.close}
      disableCloseOnEsc={disabled}
      disableCloseOnOutsideClick={disabled}
    >
      <ModalSheetHeader withCloseButton />

      <ModalSheetContent>
        <ModalSheetContentBlock>
          <ModalSheetContentTitle>
            <div className={styles.header}>Add coupon</div>
          </ModalSheetContentTitle>

          <ModalSheetContentDescription>
            <div className={styles.header}>
              Search for coupon by either coupon code or name.
            </div>
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>

        <div className={styles.searchContainer}>
          <SearchField
            autoFocus
            stretch
            dataTest="job-coupon-apply-search"
            className={styles.couponSearch}
            placeholder="Type to search"
            onChange={(searchTerm) => vm.searchCoupon(searchTerm)}
          />
        </div>

        <div className={styles.couponContainer}>
          {vm.coupons.map((coupon) => {
            return (
              <CouponItem
                key={coupon.id}
                coupon={coupon}
                selectedCouponId={vm.selectedCouponId}
                onCouponSelected={vm.setCoupon}
              />
            )
          })}

          {vm.coupons.length === 0 && (
            <div className={styles.empty}>
              <p>We couldn't find anything matching your description.</p>
              <p>Try a different search.</p>
            </div>
          )}

          {vm.showing && !vm.scrollProvider.isDone && (
            <div className={cx(styles.loading)} ref={scrollMonitorRef}>
              <Spinner />
            </div>
          )}
        </div>
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button type="default" onClick={vm.close}>
            Close
          </Button>
          <Button
            type="primary"
            loading={vm.applyCouponAsAdmin.pending}
            data-test="apply-coupon-as-admin-submit-button"
            onClick={vm.applyCouponAsAdmin}
            disabled={!vm.selectedCouponId}
          >
            Apply
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

const CouponItem = observer(function CouponItem({
  coupon,
  selectedCouponId,
  onCouponSelected,
}: {
  coupon: Coupon
  selectedCouponId: Id
  onCouponSelected: (coupon: Coupon) => void
}) {
  const { offType, maxOff } = coupon

  const discount =
    offType.type === 'AMOUNT_OFF'
      ? `${formatMoney(offType.amountOff)} off`
      : `${offType.percentOff}% off`

  const maxSavings =
    maxOff?.type === 'MAX_AMOUNT_OFF'
      ? maxOff.amountOff
        ? `${formatMoney(maxOff.amountOff)}`
        : null
      : maxOff?.percentOff
        ? `${maxOff?.percentOff}%`
        : null

  return (
    <a
      onClick={() => onCouponSelected(coupon)}
      className={styles.couponItem}
      data-test={'job-coupon-item-' + coupon.code}
    >
      <div className={styles.couponSelector}>
        <Icon
          name={coupon.id === selectedCouponId ? 'radio-selected' : 'radio'}
          color="var(--brand-primary)"
        />
      </div>

      <div className={styles.couponContent}>
        <p className={styles.couponTitle}>
          <strong>{coupon.code}</strong>
          <span>
            {coupon.dateExpires
              ? ` • Expires on ${formatDate(coupon.dateExpires)}`
              : ''}
          </span>
        </p>

        {maxSavings && <p> Maximum savings of {maxSavings}</p>}

        {coupon.description && <p>{coupon.description}</p>}
      </div>

      <div className={styles.couponDiscount}>
        <span>{discount}</span>
      </div>
    </a>
  )
})

const defaultDateFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
})

/**
 * Formats the date to a string
 * @param date
 * @returns
 */
export function formatDate(date: Date) {
  return defaultDateFormatter.format(date)
}
